import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import "../styles/user_management_styles.css";
import Navbar from "./Navbar.js";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import Table from "./Table.js";
import useFetch from "./useFetch.js";
import swal from "sweetalert";
import axios from "axios";
import config from "../config.js";
import Cookie from "universal-cookie";
import mixpanel from "mixpanel-browser";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "200px",
    overflow: "auto",
  },
  tableContainer: {
    maxWidth: "100%",
    overflowX: "auto",
  },
}));

class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center h4 mb-3">
          Something went wrong. Please try again.
        </div>
      );
    }
    return this.props.children;
  }
}

const Views_Edit = () => {
  const cookies = new Cookie();
  let userToken = "";
  let actionInitiatingUser = -1;
  try {
    userToken = cookies.get("userToken");
    actionInitiatingUser = parseInt(cookies.get("userID"));
  } catch (error) {
    console.error("Error reading cookies:", error);
  }
  const headers = {
    Authorization: `Bearer ${userToken}`,
    "Content-Type": "application/json",
  };
  const classes = useStyles();
  const {
    data: views,
    isPending: isPendingViews,
    error: errorViews,
  } = useFetch({
    actionInitiatingUser: { userID: actionInitiatingUser.toString() },
    URL: config.BACKEND_URL + "/views",
  });
  const {
    data: sources,
    isPending: isPendingSources,
    error: errorSources,
  } = useFetch({
    actionInitiatingUser: { userID: actionInitiatingUser.toString() },
    URL: config.BACKEND_URL + "/logger_sources/sources",
  });
  const [currentViewName, setCurrentViewName] = useState("");
  const [currentView, setCurrentView] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    table: false,
    chart: false,
  });
  const tempStartDateTime = useRef("");
  const tempEndDateTime = useRef("");
  const selectedRows = useRef([]);
  const tableRef = useRef(null);
  const areDatesValid = (firstDate = "", secondDate = "") => {
    return (
      firstDate.length === 0 ||
      secondDate.length === 0 ||
      firstDate <= secondDate
    );
  };
  const [userGroup, setUserGroup] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [stateOption, setStateOption] = useState([]);
  const [tableInitialized, setTableInitialized] = useState(false);

  useEffect(() => {
    if (views) {
      // console.log("Fetched views:", views);
    }
    if (sources?.data) {
      // console.log("Sources Data:", sources.data);
      // console.log("Sources Columns:", sources.columns);
      setTableInitialized(true);
    }
  }, [views, sources]);

  const tableData = useMemo(() => sources?.data || [], [sources]);
  const tableColumns = useMemo(() => {
    const columns = sources?.columns || [];
    return columns.map((column) => {
      if (column.field === "data") {
        return {
          ...column,
          title: "Topic",
          formatter: (cell) => {
            const value = cell.getValue();
            if (value && typeof value === "object") {
              if (value.topic) {
                return value.topic;
              }
              return JSON.stringify(value);
            }
            return String(value || "N/A");
          },
          sorter: (a, b) => {
            const aStr =
              a && typeof a === "object"
                ? a.topic || JSON.stringify(a)
                : String(a || "");
            const bStr =
              b && typeof b === "object"
                ? b.topic || JSON.stringify(b)
                : String(a || "");
            return aStr.localeCompare(bStr);
          },
          width: 200,
          resizable: true,
        };
      }
      if (column.field === "createdAt" || column.field === "CREATEDAT") {
        return {
          ...column,
          title: "Created At",
          formatter: (cell) => {
            const value = cell.getValue();
            if (!value) return "N/A";
            try {
              const date = new Date(value);
              return date.toLocaleString();
            } catch {
              return String(value);
            }
          },
          sorter: "datetime",
          sorterParams: {
            format: "yyyy-MM-dd'T'HH:mm:ss.SSSZ",
          },
          width: 150,
          resizable: true,
        };
      }
      if (column.field === "updatedAt" || column.field === "UPDATEDAT") {
        return {
          ...column,
          title: "Updated At",
          formatter: (cell) => {
            const value = cell.getValue();
            if (!value) return "N/A";
            try {
              const date = new Date(value);
              return date.toLocaleString();
            } catch {
              return String(value);
            }
          },
          sorter: "datetime",
          sorterParams: {
            format: "yyyy-MM-dd'T'HH:mm:ss.SSSZ",
          },
          width: 150,
          resizable: true,
        };
      }
      return {
        ...column,
        width: column.width || 150,
        resizable: true,
      };
    });
  }, [sources]);

  const fetchUG = async () => {
    try {
      const res = await axios.post(
        config.BACKEND_URL + "/views/user-group",
        { userId: actionInitiatingUser },
        { headers }
      );
      const tugs = res.data.map((ug) => ({ id: ug.id, name: ug.name }));
      setUserGroup(tugs);
      setStateOption(Array(tugs.length).fill(true));
    } catch (err) {
      console.error("Error fetching user groups:", err);
      swal("Error", "Failed to fetch user groups.", "error");
    }
  };

  const handleStartTime = (e) => {
    const value = e?.target?.value || "";
    tempStartDateTime.current = value;
    if (!value) {
      swal(
        "Start time cannot be unspecified!",
        "Please specify a start date-time.",
        "error"
      );
    } else if (
      tempEndDateTime.current &&
      !areDatesValid(value, tempEndDateTime.current)
    ) {
      swal(
        "Start time must be before end time!",
        "Please specify valid start time.",
        "error"
      );
    }
  };

  const handleEndTime = (e) => {
    const value = e?.target?.value || "";
    tempEndDateTime.current = value;
    if (!value) {
      swal(
        "End time cannot be unspecified!",
        "Please specify an end date-time.",
        "error"
      );
    } else if (
      tempStartDateTime.current &&
      !areDatesValid(tempStartDateTime.current, value)
    ) {
      swal(
        "End time must be after start time!",
        "Please specify a valid end time.",
        "error"
      );
    }
  };

  const safelySelectRows = useCallback((sourceIDs) => {
    if (!tableRef.current || !sourceIDs || sourceIDs.length === 0) return;
    try {
      tableRef.current.selectRow(sourceIDs);
    } catch (err) {
      console.error("Error selecting rows:", err);
    }
  }, []);

  const fillDetails = useCallback(() => {
    if (currentView) {
      const startDate = formatDateForInput(currentView.timeStamps?.start);
      const endDate = formatDateForInput(currentView.timeStamps?.end);
      setFormData({
        name: currentView.name || "",
        startDate,
        endDate,
        table: currentView.table || false,
        chart: currentView.chart || false,
      });
      tempStartDateTime.current = startDate;
      tempEndDateTime.current = endDate;
      if (sources?.data && currentView.sourceIDs) {
        selectedRows.current = sources.data.filter((row) =>
          currentView.sourceIDs.includes(row.id)
        );
        safelySelectRows(currentView.sourceIDs);
      }
    } else {
      setFormData({
        name: "",
        startDate: "",
        endDate: "",
        table: false,
        chart: false,
      });
      tempStartDateTime.current = "";
      tempEndDateTime.current = "";
      selectedRows.current = [];
      if (tableRef.current) {
        try {
          tableRef.current.deselectRow();
        } catch (err) {
          console.error("Error deselecting rows:", err);
        }
      }
    }
  }, [currentView, sources, safelySelectRows]);

  useEffect(() => {
    if (currentView?.sourceIDs?.length > 0) {
      safelySelectRows(currentView.sourceIDs);
    }
  }, [currentView, tableInitialized, safelySelectRows]);

  const findView = useCallback(() => {
    if (currentViewName && views) {
      const view = views.find((v) => v.name === currentViewName);
      setCurrentView(view || null);
    } else {
      setCurrentView(null);
    }
  }, [currentViewName, views]);

  const setViewName = useCallback((value) => {
    const safeValue = typeof value === "string" ? value : "";
    if (safeValue === "selectViewText" || !safeValue) {
      setCurrentViewName("");
    } else {
      setCurrentViewName(safeValue);
    }
  }, []);

  const handleFormChange = (field) => (e) => {
    const value = e?.target?.value || "";
    if (field === "viewName") {
      setViewName(value);
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleCheckboxChange = (field) => (e) => {
    const checked = e?.target?.checked || false;
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  const formatDateForInput = (utcDateString) => {
    if (!utcDateString) return "";
    const date = new Date(utcDateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const setSelectedRows = (rows) => {
    selectedRows.current = rows;
  };

  useEffect(() => {
    findView();
  }, [currentViewName, views, findView]);

  useEffect(() => {
    fillDetails();
  }, [currentView, fillDetails]);

  useEffect(() => {
    if (currentView) {
      fetchUG();
    }
    return () => {
      if (tableRef.current) {
        try {
          tableRef.current.destroy();
        } catch (err) {
          console.error("Error destroying table:", err);
        }
      }
    };
  }, [currentView]);

  const isUGSelected = () => {
    return stateOption.some((option) => option);
  };

  const handleViewsEdit = (e) => {
    e.preventDefault();
    mixpanel.track("View Edit Button Clicked", {
      userID: cookies.get("userID"),
    });
    const { name, startDate: start, endDate: end, table, chart } = formData;
    tempStartDateTime.current = start;
    tempEndDateTime.current = end;

    if (!start || !end) {
      swal(
        "Start time and end time cannot be unspecified!",
        "Please specify valid date-times.",
        "error"
      );
      return;
    }

    if (areDatesValid(start, end)) {
      if (!currentView?.id) {
        swal(
          "View not selected!",
          "Please select a view before editing it!",
          "error"
        );
        return;
      }
      if (userGroup.length > 0 && !isUGSelected()) {
        swal("Please Select at least one User Group", "", "error");
        return;
      }
      const filteredUserGroup = userGroup.filter(
        (_, index) => stateOption[index]
      );
      const sourceIDs = selectedRows.current.map((row) => row.id);
      if (sourceIDs.length === 0) {
        swal(
          "No sources selected!",
          "Please select at least one source.",
          "error"
        );
        return;
      }
      const body = {
        name,
        table,
        chart,
        timeStamps: {
          start,
          end,
          offsetMinutes: new Date().getTimezoneOffset(),
        },
        sourceIDs,
        actionInitiatingUser,
        filteredUserGroup,
      };
      axios
        .patch(config.BACKEND_URL + `/views/${currentView.id}`, body, {
          headers,
        })
        .then((res) => {
          swal("Success!", "View edited successfully!", "success").then(() => {
            window.location.reload();
          });
        })
        .catch((err) => {
          console.error("Error updating view:", err);
          if (err.response?.data?.name === "ViewAlreadyExists") {
            swal(
              "Could not update the view!",
              "A view with the given name already exists. Please enter a new name.",
              "error"
            );
          } else {
            swal(
              "Could not update the view!",
              err.response?.data?.message || "Some error has occurred.",
              "error"
            );
          }
        });
    } else {
      swal(
        "Please enter valid dates!",
        "Ending date must be greater than or equal to the start date",
        "error"
      );
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleClear = () => {
    setStateOption(Array(userGroup.length).fill(false));
  };

  const handleAll = () => {
    setStateOption(Array(userGroup.length).fill(true));
  };

  const handleChangeDialog = (e, index) => {
    const isChecked = e?.target?.checked || false;
    const newArr = [...stateOption];
    newArr[index] = isChecked;
    setStateOption(newArr);
  };

  const handleReset = () => {
    setFormData({
      name: "",
      startDate: "",
      endDate: "",
      table: false,
      chart: false,
    });
    tempStartDateTime.current = "";
    tempEndDateTime.current = "";
    selectedRows.current = [];
    setCurrentViewName("");
    setCurrentView(null);
    setStateOption(Array(userGroup.length).fill(false));
    if (tableRef.current) {
      try {
        tableRef.current.deselectRow();
      } catch (err) {
        console.error("Error deselecting rows on reset:", err);
      }
    }
  };

  const dialogContent = useMemo(() => {
    return userGroup.map((ug, index) => (
      <FormControlLabel
        key={ug.id}
        control={
          <Checkbox
            checked={stateOption[index] || false}
            onChange={(e) => handleChangeDialog(e, index)}
            name={ug.name}
            color="primary"
          />
        }
        label={ug.name}
      />
    ));
  }, [userGroup, stateOption]);

  const tableEvents = useMemo(() => {
    return {
      tableBuilt: function (table) {
        setTableInitialized(true);
      },
    };
  }, []);

  // ----------------- MQTT-like inline styling (kept inside component) -----------------
  const mqttStyles = `
    /* Root layout */
    .mqtt-root { background: linear-gradient(180deg, #071028 0%, #0b1323 100%); min-height: 100vh; }
    .mqtt-container { max-width: 1200px; margin: 24px auto; padding: 18px; }

    /* Card */
    .mqtt-card {
      background: rgba(15,23,42,0.65);
      color: #e6eef8;
      border-radius: 14px;
      border: 1px solid rgba(148,163,184,0.08);
      box-shadow: 0 6px 20px rgba(2,6,23,0.6), inset 0 1px 0 rgba(255,255,255,0.02);
      padding: 18px;
      margin-bottom: 18px;
    }
    .mqtt-card.header { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
    .mqtt-title { font-size:1.4rem; font-weight:700; color:#e6eef8; margin:0; }
    .mqtt-sub { color: rgba(226,232,240,0.7); margin-top:6px; }

    /* Form grid */
    .mqtt-form-row { display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
    .mqtt-field { flex:1 1 320px; min-width:160px; }
    label.mqtt-label { display:block; color:rgba(203,213,225,0.8); font-weight:600; margin-bottom:6px; }

    /* inputs */
    .mqtt-input, .mqtt-select, .mqtt-datetime {
      width:100%;
      padding:10px 12px;
      border-radius:10px;
      background: rgba(255,255,255,0.03);
      border: 1px solid rgba(148,163,184,0.08);
      color: #e6eef8;
      outline:none;
      transition: box-shadow .15s, border-color .12s, transform .06s;
      font-weight:500;
    }
    .mqtt-input::placeholder { color: rgba(226,232,240,0.35); }
    .mqtt-input:focus, .mqtt-select:focus, .mqtt-datetime:focus {
      border-color: #60a5fa;
      box-shadow: 0 6px 20px rgba(59,130,246,0.08);
      background: rgba(255,255,255,0.02);
    }

    /* Buttons */
    .mqtt-btn-row { display:flex; gap:12px; flex-wrap:wrap; justify-content:center; margin-top:12px; }
    .mqtt-btn { padding:10px 18px; border-radius:10px; border:none; font-weight:600; cursor:pointer; }
    .mqtt-btn.primary { background: linear-gradient(90deg,#3b82f6,#2563eb); color:white; box-shadow: 0 8px 20px rgba(37,99,235,0.18); }
    .mqtt-btn.ghost { background: transparent; border: 1px solid rgba(148,163,184,0.08); color: #e6eef8; }
    .mqtt-btn.warn { background: linear-gradient(90deg,#ef4444,#f97316); color:white; }

    /* small helpers */
    .mqtt-meta { color: rgba(148,163,184,0.9); font-size:0.95rem; }
    .mqtt-badge { background: rgba(99,102,241,0.12); color:#a5b4fc; padding:6px 10px; border-radius:999px; font-weight:600; }

    /* Table wrapper */
    .mqtt-table-wrap { margin-top:12px; border-radius:12px; overflow:hidden; border:1px solid rgba(148,163,184,0.04); background: rgba(9,10,20,0.3); padding:10px; }
    .mqtt-table-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; }

    /* Dialog tweaks - MUI will still control, but we can tone colors */
    .MuiDialog-paper { background: rgba(15,23,42,0.95) !important; color: #e6eef8 !important; border-radius:12px !important; }

    /* Responsive */
    @media (max-width: 980px) {
      .mqtt-card { padding: 14px; }
      .mqtt-title { font-size:1.15rem; }
      .mqtt-form-row { flex-direction:column; align-items:stretch; }
      .mqtt-field { min-width:unset; }
      .mqtt-btn-row { justify-content:stretch; }
      .mqtt-btn { width:100%; }
    }

    @media (max-width: 520px) {
      .mqtt-container { padding: 12px; }
      .mqtt-card { padding: 12px; border-radius:10px; }
      .mqtt-title { font-size:1rem; }
      .mqtt-sub { font-size:0.88rem; }
    }
  `;

  return (
    <ErrorBoundary>
      <div className="Views mqtt-root">
        <style dangerouslySetInnerHTML={{ __html: mqttStyles }} />
        <link
          href="https://fonts.googleapis.com/icon?family=Material+Icons"
          rel="stylesheet"
        />
        <Navbar />
        <main className="mqtt-container">
          <section className="mqtt-card header">
            <div style={{ minWidth: 0 }}>
              <h1 className="mqtt-title">Edit View</h1>
              <div className="mqtt-sub">Select the view you wish to edit.</div>
              <div className="mqtt-meta" style={{ marginTop: 6 }}>
                Note: Timestamps are displayed in your local time but stored in
                UTC.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginTop: 6,
              }}
            >
              {isPendingViews ? (
                <div className="mqtt-meta">Loading views...</div>
              ) : errorViews ? (
                <div className="mqtt-meta">
                  Cannot fetch all the views right now!
                </div>
              ) : views && views.length > 0 ? (
                <select
                  onChange={handleFormChange("viewName")}
                  className="mqtt-select"
                  style={{ minWidth: 220 }}
                  name="views"
                  id="views"
                  value={currentViewName || ""}
                >
                  <option value="selectViewText">Choose view</option>
                  {views.map((view, index) => (
                    <option key={index} value={view.name}>
                      {view.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mqtt-meta">No views available</div>
              )}
            </div>
          </section>

          {views && views.length > 0 && (
            <section className="mqtt-card" style={{ paddingBottom: 14 }}>
              <form id="editForm" onSubmit={handleViewsEdit}>
                <div
                  className="mqtt-form-row"
                  style={{ alignItems: "flex-end" }}
                >
                  <div className="mqtt-field">
                    <label className="mqtt-label" htmlFor="startDate">
                      From
                    </label>
                    <input
                      className="mqtt-datetime"
                      type="datetime-local"
                      name="startDate"
                      id="startDate"
                      value={formData.startDate}
                      onChange={(e) => {
                        handleFormChange("startDate")(e);
                        handleStartTime(e);
                      }}
                    />
                  </div>

                  <div className="mqtt-field">
                    <label className="mqtt-label" htmlFor="endDate">
                      To
                    </label>
                    <input
                      className="mqtt-datetime"
                      type="datetime-local"
                      name="endDate"
                      id="endDate"
                      value={formData.endDate}
                      onChange={(e) => {
                        handleFormChange("endDate")(e);
                        handleEndTime(e);
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{ marginTop: 12 }}
                  className="mqtt-card"
                  aria-hidden
                >
                  <label className="mqtt-label">Enter name of the view</label>
                  <input
                    type="text"
                    id="name"
                    className="mqtt-input"
                    autoComplete="off"
                    required
                    style={{ fontWeight: "600" }}
                    value={formData.name}
                    onChange={handleFormChange("name")}
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  <div
                    className="mqtt-meta"
                    style={{ marginBottom: 8, textAlign: "center" }}
                  >
                    What do you wish to include in the view?
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      justifyContent: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        className="form-check-input"
                        name="storeDetails[]"
                        type="checkbox"
                        id="tableInput"
                        checked={formData.table}
                        onChange={handleCheckboxChange("table")}
                      />
                      <span style={{ color: "#e6eef8", fontWeight: 600 }}>
                        Table
                      </span>
                    </label>

                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        className="form-check-input"
                        name="storeDetails[]"
                        type="checkbox"
                        id="chartInput"
                        checked={formData.chart}
                        onChange={handleCheckboxChange("chart")}
                      />
                      <span style={{ color: "#e6eef8", fontWeight: 600 }}>
                        Chart
                      </span>
                    </label>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div className="mqtt-meta" style={{ textAlign: "center" }}>
                      Please Select User Group to which you want to assign this
                      view*
                    </div>
                  </div>
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleOpenDialog}
                      style={{ borderRadius: 10 }}
                    >
                      Open User Group
                    </Button>
                  </div>
                </div>

                <div className="mqtt-table-wrap" style={{ marginTop: 14 }}>
                  {isPendingSources && (
                    <div className="mqtt-meta">Loading sources...</div>
                  )}
                  {errorSources && (
                    <div className="mqtt-meta">
                      Cannot fetch the sources right now!
                    </div>
                  )}
                  {sources && sources.columns && sources.columns.length > 0 && (
                    <div className="mqtt-table-scroll">
                      <div style={{ minWidth: 780 }}>
                        <Table
                          tableData={tableData}
                          tableColumns={tableColumns}
                          onSelectionChange={setSelectedRows}
                          shouldModifySources={false}
                          shouldDownload={false}
                          isSelectable={true}
                          tableID="ViewsTable"
                          tableRef={tableRef}
                          layout="fitDataFill"
                          maxWidth="100%"
                          autoResize={false}
                          events={tableEvents}
                          highlightedRows={currentView?.sourceIDs || []}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mqtt-btn-row" style={{ marginTop: 16 }}>
                  <button className="mqtt-btn primary" type="submit">
                    Submit
                  </button>
                  <button
                    className="mqtt-btn ghost"
                    type="reset"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
              </form>

              <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                aria-labelledby="form-dialog-title"
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle
                  id="form-dialog-title"
                  style={{ color: "#e6eef8" }}
                >
                  Below are the list of User Group in which you are in
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                  {dialogContent}
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={handleClear} color="primary">
                    Clear
                  </Button>
                  <Button onClick={handleAll} color="primary">
                    All
                  </Button>
                  <Button onClick={handleCloseDialog} color="primary">
                    Ok
                  </Button>
                </DialogActions>
              </Dialog>
            </section>
          )}
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default Views_Edit;
